import { Button, SimplePagination } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, Eye, HelpCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { SimpleSelectOption } from "@/shared/ui/SimpleSelect";
import { useSopListQuery } from "../api/queries";
import {
  sliceTrainingListPage,
  TRAINING_LIST_PAGE_SIZE,
  trainingListTotalPages,
} from "../lib/trainingListPagination";
import { TrainingTour } from "../tour/TrainingTour";
import { sopListTourSteps } from "../tour/trainingTourSteps";
import { useTrainingTour } from "../tour/useTrainingTour";
import { trainingRoutes } from "../trainingRoutes";
import { TrainingListControls } from "../ui/TrainingListControls";
import { TrainingResourceCardGridSkeleton } from "../ui/TrainingResourceCardGridSkeleton";

const SOP_SORT_OPTIONS: SimpleSelectOption[] = [
  { value: "title", label: "ຫົວຂໍ້" },
  { value: "departmentName", label: "ພະແນກ" },
  { value: "purpose", label: "ຈຸດປະສົງ" },
];

const SOP_SORT_IDS = new Set(SOP_SORT_OPTIONS.map((o) => o.value));

export function SopListPage() {
  const tour = useTrainingTour("training-sop-list-tour-completed");
  const navigate = useNavigate({ from: "/app/training/sop" });
  const routeSearch = useSearch({ from: "/app/training/sop" });
  const rawSortBy = routeSearch.sortBy ?? "title";
  const sortBy = SOP_SORT_IDS.has(rawSortBy) ? rawSortBy : "title";
  const sortOrder: "asc" | "desc" =
    routeSearch.sortOrder === "desc" ? "desc" : "asc";
  const { data, isLoading } = useSopListQuery(sortBy, sortOrder);
  const rows = (data?.data ?? []) as Array<{
    id: string;
    title: string;
    purpose: string | null;
    departmentName: string | null;
  }>;
  const [filterQuery, setFilterQuery] = useState("");
  const filteredRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const title = row.title.toLowerCase();
      const purpose = (row.purpose ?? "").toLowerCase();
      const department = (row.departmentName ?? "").toLowerCase();
      return title.includes(q) || purpose.includes(q) || department.includes(q);
    });
  }, [rows, filterQuery]);

  const [page, setPage] = useState(1);
  const totalPages = trainingListTotalPages(filteredRows.length);
  const safePage = Math.min(page, totalPages);
  const listResetKey = `${filterQuery}\0${sortBy}\0${sortOrder}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: listResetKey encodes filter + sort; reset page when it changes
  useEffect(() => {
    setPage(1);
  }, [listResetKey]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(
    () => sliceTrainingListPage(filteredRows, safePage),
    [filteredRows, safePage],
  );

  const emptyMessage =
    rows.length === 0
      ? "ບໍ່ມີຂັ້ນຕອນການດຳເນີນງານທີ່ກ່ຽວຂ້ອງກັບຕຳແໜ່ງຂອງທ່ານ"
      : "ບໍ່ພົບ SOP ທີ່ກົງກັບການຄົ້ນຫາ";

  return (
    <>
      <Header />
      <Main>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h1 className="font-bold text-2xl sm:text-3xl">
                ຂັ້ນຕອນການດຳເນີນງານ
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:mb-4 sm:w-auto"
            onClick={() =>
              navigate({
                from: "/app/training/sop",
                to: trainingRoutes.exitToAppHome,
              })
            }
          >
            <ArrowLeftIcon className="h-4 w-4" /> ກັບຄືນ
          </Button>
        </div>

        <TrainingListControls
          filterInputId="sop-list-search"
          filterLabel="ຄົ້ນຫາ SOP"
          filterQuery={filterQuery}
          onFilterChange={setFilterQuery}
          searchPlaceholder="ຄົ້ນຫາ SOP..."
          filteredCount={filteredRows.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          sortOptions={SOP_SORT_OPTIONS}
          onSortByChange={(next) =>
            navigate({
              search: {
                ...routeSearch,
                sortBy: next,
              },
            })
          }
          onSortOrderToggle={() =>
            navigate({
              search: {
                ...routeSearch,
                sortOrder: sortOrder === "asc" ? "desc" : "asc",
              },
            })
          }
          searchTourId="training-sop-list-search"
        />

        <div
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
          data-tourid="training-sop-list-grid"
        >
          {isLoading ? (
            <TrainingResourceCardGridSkeleton />
          ) : filteredRows.length === 0 ? (
            <p className="col-span-full py-14 text-center text-muted-foreground text-sm">
              {emptyMessage}
            </p>
          ) : (
            pageItems.map((row) => (
              <button
                key={row.id}
                type="button"
                className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-colors hover:border-primary/30 hover:bg-muted/25"
                onClick={() =>
                  navigate({
                    from: "/app/training/sop",
                    to: trainingRoutes.sopDetail,
                    params: { id: row.id },
                  })
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-semibold text-base leading-snug group-hover:text-primary">
                    {row.title}
                  </h2>
                  <Eye
                    className="size-4 shrink-0 text-muted-foreground group-hover:text-primary"
                    aria-hidden
                  />
                </div>
                <p className="mt-2 text-muted-foreground text-xs">
                  ພະແນກ · {row.departmentName?.trim() || "—"}
                </p>
                <p className="mt-3 line-clamp-3 text-muted-foreground text-sm leading-relaxed">
                  {row.purpose?.trim() || "—"}
                </p>
              </button>
            ))
          )}
        </div>

        {!isLoading && filteredRows.length > 0 && totalPages > 1 ? (
          <div className="mt-8 flex justify-center">
            <SimplePagination
              page={safePage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        ) : null}

        {!isLoading && filteredRows.length > 0 ? (
          <p className="mt-4 text-center text-muted-foreground text-xs">
            {totalPages > 1
              ? `ໜ້າ ${safePage} ຈາກ ${totalPages} · ສູງສຸດ ${TRAINING_LIST_PAGE_SIZE} ລາຍການຕໍ່ໜ້າ`
              : `${filteredRows.length} ລາຍການ`}
          </p>
        ) : null}

        <TrainingTour
          steps={sopListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
